/**
 * Local persistence for the terminal chat transcripts.
 *
 * The goal is Telegram-style continuity: a refresh repaints the most recent
 * messages immediately so the user has something to scroll while the gateway
 * reconnects, instead of staring at a blank chat.
 *
 * OpenClaw's `chat.history` RPC is a tail-reader with no cursor pagination
 * (hard max 1000 messages), so the local cache is the primary source of
 * deeper history — caps are intentionally generous to retain weeks of
 * conversation across reloads.
 *
 * Sessions: each `ChatSession` has its own per-session message blob keyed
 * via `config.keyFor('messages', accountId, sessionId)`. The sessions
 * index lives under `config.keyFor('sessions', accountId)`.
 *
 * Hosts call `createChatHistoryStore(config)` with their own key-shape
 * function and feature toggles so existing user data continues to load:
 *
 *   Paradex (prefix-style):  `paradex_terminal_<scope>:<addr>[:<extra>]`
 *   Paradigm (suffix-style): `<userId>:paradigm_terminal_<scope>[:<extra>]`
 *
 * Per-host divergences captured via config:
 *   - `legacyMigration`: 'one-level' (Paradex) | 'two-level' (Desktop)
 *   - `pendingCleanupTracking`: true only for Desktop
 *   - `orphanScanOnClear`: true only for Desktop
 *   - `clearCustomFoldersOnSignOut`: true only for Desktop
 */

import type { GatewayHistoryRow } from './gateway-history';
import { isSlashCommandText } from './gateway-sessions';
import { SCOPED_CHAT_ID_MARKER } from './session-keys';
import type {
  ChatMessage,
  ChatSession,
  CustomFolder,
  MessageAttachment,
  SessionFolder,
  ToolCall,
} from './types.js';

// --- Configuration ---

export type ChatHistoryScope =
  | 'sessions'
  | 'customFolders'
  | 'messages'
  | 'transcript'
  | 'historyUi'
  | 'tombstones'
  | 'legacyHistory'
  | 'veryLegacyHistory'
  | 'pendingCleanup';

export interface ChatHistoryStoreConfig {
  /**
   * Compute the localStorage key for a given (scope, accountId, ...extra)
   * tuple. Extra parts are typically `sessionId` for per-session message
   * blobs.
   *
   * Paradex example:
   *   keyFor('messages', '0xabc', 'session-1')
   *   → 'paradex_terminal_chat_history:0xabc:session-1'
   *
   * Desktop example:
   *   keyFor('messages', 'user-42', 'session-1')
   *   → 'user-42:paradigm_terminal_chat_history:session-1'
   */
  keyFor(
    scope: ChatHistoryScope,
    accountId: string,
    ...extra: string[]
  ): string;

  /** Default folder for sessions created/migrated by this host. */
  defaultFolder: SessionFolder;

  /**
   * Legacy migration depth.
   * - `'one-level'` — try only the current key, ignore historical shapes.
   * - `'two-level'` — try current, then `veryLegacyHistory` and migrate.
   */
  legacyMigration?: 'one-level' | 'two-level';

  /** Off in Paradex; Desktop sets true and uses the pending-cleanup APIs. */
  pendingCleanupTracking?: boolean;

  /** Off in Paradex; Desktop sets true for defensive prefix scans. */
  orphanScanOnClear?: boolean;

  /** Off in Paradex; Desktop sets true so sign-out also wipes folders. */
  clearCustomFoldersOnSignOut?: boolean;
}

// --- Public constants (re-exported by hosts) ---

/**
 * Absolute safety cap. Per-origin localStorage caps at ~5MB and a single
 * chat message can easily exceed 1KB.
 */
export const PERSISTED_HISTORY_HARD_CAP = 1500;
/**
 * Tool calls kept per run — a sliding window over the tail, with the overflow
 * counted in `toolCallsDropped`. Nothing upstream caps how many tools a run
 * calls, and the array is re-serialised on every transcript write; 200 is far
 * above a real agentic turn (~4 KB of JSON at the cap).
 *
 * Declared here, not beside the reducer, because logs also arrive from sync
 * docs and older blobs — `sanitizeMessageToolCalls` re-enforces both bounds.
 */
export const TOOL_CALLS_WINDOW = 200;

/** Longest tool name persisted — a real one is an identifier. */
export const TOOL_NAME_MAX_CHARS = 80;

/**
 * Keys a persisted `ToolCall` may carry. Both the foreign-key check and the
 * rebuild's projection run off this list; adding a field to `ToolCall` means
 * editing it here AND adding its check to the entry gate below, which
 * validates `name` only — the projection copies an unvalidated key straight
 * out of an untrusted blob. Named rather than a key count, which would
 * silently strip a new field from records written by a newer build.
 */
const TOOL_CALL_KEYS = ['name'] as const;

/**
 * Total attachment-thumbnail (`previewUrl`) bytes persisted per session.
 * Each thumbnail is ~8–20KB and message retention is count-based (1500), so
 * without a byte budget a screenshot-heavy session overflows the ~5MB
 * localStorage origin quota and the sidecar's 8MiB session-doc cap — both
 * silently, and the 413 on the latter permanently drops the session from
 * sync. Newest previews win; older messages keep their attachment metadata
 * (chips render as labels) but lose the inline thumbnail.
 */
export const PERSISTED_THUMBNAIL_BYTE_BUDGET = 512 * 1024;
/**
 * Total reasoning (`thinking`) UTF-8 bytes persisted per session. Same problem
 * as thumbnails above: retention is count-based, and with thinking on by
 * default a reasoning model emits a few KB per turn, so 1500 messages of
 * unbounded traces would overflow the localStorage quota and the sidecar's
 * 8MiB session-doc cap. Newest traces win; an older message that carries an
 * answer keeps it and loses only the block, and one that was nothing BUT a
 * block is dropped (see `trimMessagesForPersistence`).
 *
 * Measured in real UTF-8 bytes rather than UTF-16 code units, unlike the
 * thumbnail budget above — a `data:` URL is base64 ASCII, where the two agree,
 * but a reasoning trace is natural language, and a CJK-heavy one is 3 bytes
 * per code unit. Counting units would let a nominal 256 KB session weigh
 * ~768 KB against the byte-denominated cap this budget exists to respect.
 *
 * NOTE both budgets are per SESSION while the localStorage quota they defend
 * is per ORIGIN: 30 retained sessions × (512 KB + 256 KB) is ~23 MB against a
 * ~5 MB quota. `saveSessionMessages` swallows `QuotaExceededError`, so the
 * failure mode is the active session quietly not persisting. Pre-existing, and
 * an origin-level fallback (trim the oldest sessions on write failure) is the
 * real fix; this note is here so the next person raising either number knows
 * which ceiling actually binds.
 */
export const PERSISTED_THINKING_BYTE_BUDGET = 256 * 1024;
/** Maximum stored length for an auto-derived session title. */
export const SESSION_TITLE_MAX_LEN = 200;
/** Hard cap on pending-cleanup retention (Desktop-only). */
export const PENDING_CLEANUP_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// --- Public types ---

export interface HistoryUiState {
  readonly collapsed: {
    readonly paradex: boolean;
    readonly paradigm: boolean;
  };
  readonly activeSessionId: string | null;
}

const DEFAULT_UI_STATE: HistoryUiState = {
  collapsed: { paradex: false, paradigm: false },
  activeSessionId: null,
};

/**
 * Deletion marker for session-sync. Tombstones ride inside the synced
 * index document so a deletion on this device beats a stale index from
 * another one; locally they survive reloads so offline deletions aren't
 * lost.
 */
export interface DeletionTombstone {
  readonly kind: 'session' | 'folder';
  readonly id: string;
  readonly deletedAt: number;
}

/** Tracked failed sign-out — Desktop-only (no-op in Paradex). */
export interface PendingCleanup {
  readonly userId: string;
  readonly signedOutAt: number;
  readonly lastAttemptAt?: number;
}

// --- Helpers (re-exported as pure functions) ---

export function generateSessionId(): string {
  return generateRandomId('s');
}

/**
 * A new CHAT's id — marked so its gateway key carries openclaw's title scope,
 * which is what earns it a generated title. See `session-keys.ts` for why the
 * marker lives in the id and why existing chats must never be re-marked.
 *
 * Distinct from `generateSessionId` because that one also mints custom-folder
 * ids, which are not sessions and must not be scoped.
 */
export function generateChatSessionId(): string {
  return `${SCOPED_CHAT_ID_MARKER}${generateSessionId()}`;
}

export function generateMessageId(prefix: string): string {
  return `${prefix}-${generateRandomId('m')}`;
}

function generateRandomId(fallbackPrefix: string): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c != null && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  // Fallback for environments without `crypto.randomUUID`. These are local
  // session/message ids — collision-only risk, NOT security tokens — so a
  // `Math.random()` suffix is acceptable here (the device-identity nonce, by
  // contrast, is server-provided, not derived from this).
  return `${fallbackPrefix}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

/**
 * Pull a recognizable title out of a transcript: the first user message,
 * collapsed to one line and clamped to `SESSION_TITLE_MAX_LEN`. Falls back
 * to the first assistant message when there's no user message yet, and to
 * `fallback` when there's no content at all.
 */
export function deriveSessionTitleFromMessages(
  messages: readonly ChatMessage[],
  fallback: string,
): string {
  // A slash command is never a name. The terminal used to write
  // `/model <id>` as the first message of every new chat, so deriving from
  // the first non-empty message named chats after their model. Skipped at
  // both ends: a command typed by the user is no better a title than one the
  // client wrote.
  const named = (m: ChatMessage): boolean =>
    m.content.trim().length > 0 && !isSlashCommandText(m.content);
  const candidate =
    messages.find((m) => m.role === 'user' && named(m)) ??
    messages.find(named) ??
    messages.find((m) => m.role === 'user' && m.content.trim().length > 0) ??
    messages.find((m) => m.content.trim().length > 0);
  if (candidate == null) return fallback;
  const oneLine = candidate.content.replace(/\s+/gu, ' ').trim();
  if (oneLine.length === 0) return fallback;
  if (oneLine.length <= SESSION_TITLE_MAX_LEN) return oneLine;
  return `${oneLine.slice(0, SESSION_TITLE_MAX_LEN - 1)}…`;
}

// --- Store shape ---

export interface ChatHistoryStore {
  loadSessions(accountId: string): readonly ChatSession[];
  saveSessions(accountId: string, sessions: readonly ChatSession[]): void;

  loadCustomFolders(accountId: string): readonly CustomFolder[];
  saveCustomFolders(accountId: string, folders: readonly CustomFolder[]): void;

  loadSessionMessages(
    accountId: string,
    sessionId: string,
  ): readonly ChatMessage[];
  /**
   * Persist a transcript, returning the trimmed slice. (Returned even when
   * the write itself failed — a swallowed `QuotaExceededError` doesn't change
   * what the trimmed shape is, and the caller wants the shape.)
   *
   * Callers that go on to publish the same transcript — the sync
   * upload — must publish THIS array, not the one they passed in: the trim is
   * what clears the live flags and enforces the byte budgets, and the doc cap
   * those budgets defend is on the wire, not on disk.
   */
  saveSessionMessages(
    accountId: string,
    sessionId: string,
    messages: readonly ChatMessage[],
  ): readonly ChatMessage[];
  deleteSessionMessages(accountId: string, sessionId: string): void;

  /**
   * Delete every blob from the retired mixed-transcript store for an account.
   *
   * Those blobs held a list this client had built itself — the agent's turns
   * with device-only bubbles mixed in — and nothing reads them now. Left
   * alone they would sit in a ~5 MB origin quota forever, crowding out the
   * paint cache that replaced them. Called once per account at launch.
   */
  purgeLegacyTranscripts(accountId: string): void;

  /**
   * The agent's rows for a chat, exactly as its last `chat.history` answer
   * carried them. For the FIRST PAINT of that chat and nothing else: project
   * them the same way a live read is projected, show the result, and let the
   * read that follows overwrite this wholesale.
   *
   * Empty when the chat has never been read on this device, which is not the
   * same as the chat being empty — the caller shows a loading state, not "no
   * messages".
   */
  loadSessionTranscript(
    accountId: string,
    sessionId: string,
  ): readonly GatewayHistoryRow[];

  /**
   * Replace the cached rows for a chat with what the agent just sent. Called
   * only from a `chat.history` answer; there is no append, no merge, and no
   * partial update, because a cache that can disagree with the source of
   * truth in more than one way is a thing to reconcile.
   */
  saveSessionTranscript(
    accountId: string,
    sessionId: string,
    rows: readonly GatewayHistoryRow[],
  ): void;

  /**
   * A number that changes whenever any chat's cached rows are written.
   *
   * For callers that cache something DERIVED from those rows and need to
   * know when to rebuild it. A chat's own `updatedAt` cannot answer this: it
   * belongs to the roster and does not move when a read replaces the cached
   * copy, so anything keyed on it goes stale silently.
   */
  transcriptCacheVersion(): number;

  /**
   * Whether a chat has any cached rows, without parsing them.
   *
   * An existence test used on the roster-read path, where the alternative
   * was loading and parsing up to a megabyte of JSON per chat on the main
   * thread purely to compare a length against zero.
   */
  hasSessionTranscript(accountId: string, sessionId: string): boolean;

  loadHistoryUiState(accountId: string): HistoryUiState;
  saveHistoryUiState(accountId: string, state: HistoryUiState): void;

  loadTombstones(accountId: string): readonly DeletionTombstone[];
  saveTombstones(
    accountId: string,
    tombstones: readonly DeletionTombstone[],
  ): void;
  addTombstone(
    accountId: string,
    kind: DeletionTombstone['kind'],
    id: string,
  ): void;

  migrateLegacyHistoryIfNeeded(accountId: string): readonly ChatSession[];
  recoverOrphanedFolders(
    sessions: readonly ChatSession[],
    existingFolders: readonly CustomFolder[],
  ): { readonly folders: readonly CustomFolder[]; readonly recovered: boolean };
  clearChatHistory(accountId: string): void;

  // Desktop-only — no-ops when `pendingCleanupTracking` is false.
  loadPendingCleanups(): readonly PendingCleanup[];
  /** Record (or refresh) a pending-cleanup entry for `userId`. Idempotent. */
  addPendingCleanup(userId: string): void;
  removePendingCleanup(userId: string): void;
  updatePendingCleanup(userId: string, patch: Partial<PendingCleanup>): void;
}

// --- Type guards ---

function isPersistedTombstone(value: unknown): value is DeletionTombstone {
  if (value == null || typeof value !== 'object') return false;
  const t = value as Record<string, unknown>;
  return (
    (t.kind === 'session' || t.kind === 'folder') &&
    typeof t.id === 'string' &&
    typeof t.deletedAt === 'number'
  );
}

function isPersistedPendingCleanup(value: unknown): value is PendingCleanup {
  if (value == null || typeof value !== 'object') return false;
  const c = value as Record<string, unknown>;
  return (
    typeof c.userId === 'string' &&
    c.userId.length > 0 &&
    typeof c.signedOutAt === 'number' &&
    Number.isFinite(c.signedOutAt) &&
    (c.lastAttemptAt === undefined || typeof c.lastAttemptAt === 'number')
  );
}

/**
 * The four fields every consumer dereferences without asking. Exported so the
 * remote-document boundary can apply the SAME predicate the localStorage
 * boundary does — a filter that only rejects non-objects lets
 * `{ id, role, timestamp }` with no `content` through, and the next reader of
 * `m.content.length` throws.
 */
export function isPersistedChatMessage(value: unknown): value is ChatMessage {
  if (value == null || typeof value !== 'object') return false;
  const m = value as Record<string, unknown>;
  return (
    typeof m.id === 'string' &&
    (m.role === 'user' || m.role === 'assistant') &&
    typeof m.content === 'string' &&
    typeof m.timestamp === 'number'
  );
}

function isPersistedCustomFolder(value: unknown): value is CustomFolder {
  if (value == null || typeof value !== 'object') return false;
  const f = value as Record<string, unknown>;
  return (
    typeof f.id === 'string' &&
    typeof f.name === 'string' &&
    typeof f.createdAt === 'number' &&
    (f.pinned === undefined || typeof f.pinned === 'boolean')
  );
}

function isPersistedSession(value: unknown): value is ChatSession {
  if (value == null || typeof value !== 'object') return false;
  const s = value as Record<string, unknown>;
  // Any non-empty string. Chats are filed under
  // `config.defaultFolder ?? config.namespace`, which is `standalone` for
  // the DIME app — so an allowlist of the two host names rejected every row
  // and discarded the index, taking renames and pins with it.
  const folder = typeof s.folder === 'string' ? s.folder.trim() : '';
  return (
    typeof s.sessionId === 'string' &&
    folder.length > 0 &&
    typeof s.title === 'string' &&
    typeof s.pinned === 'boolean' &&
    typeof s.createdAt === 'number' &&
    typeof s.updatedAt === 'number' &&
    (s.customFolderId === undefined || typeof s.customFolderId === 'string') &&
    (s.folderUpdatedAt === undefined ||
      typeof s.folderUpdatedAt === 'number') &&
    (s.clearedAt === undefined || typeof s.clearedAt === 'number')
  );
}

/**
 * Coerce a message's `attachments` on entry. They cross two unvalidated
 * boundaries — localStorage and remote sync docs — and `previewUrl` lands in
 * an `<img src>`, so entries are kept only when well-shaped, and previews
 * only as `data:image/` URLs (an off-origin URL here would issue a tracking
 * GET on render). Every other persisted field is validated; this one must be
 * too.
 */
export function sanitizeMessageAttachments(message: ChatMessage): ChatMessage {
  const raw = (message as { readonly attachments?: unknown }).attachments;
  if (raw == null) return message;
  const clean: MessageAttachment[] = [];
  if (Array.isArray(raw)) {
    for (const entry of raw as readonly unknown[]) {
      if (entry == null || typeof entry !== 'object') continue;
      const a = entry as {
        readonly type?: unknown;
        readonly mimeType?: unknown;
        readonly fileName?: unknown;
        readonly previewUrl?: unknown;
      };
      if (a.type !== 'image' && a.type !== 'file') continue;
      if (typeof a.mimeType !== 'string') continue;
      clean.push({
        type: a.type,
        mimeType: a.mimeType,
        ...(typeof a.fileName === 'string' ? { fileName: a.fileName } : {}),
        ...(typeof a.previewUrl === 'string' &&
        a.previewUrl.startsWith('data:image/')
          ? { previewUrl: a.previewUrl }
          : {}),
      });
    }
  }
  if (clean.length === 0) {
    const rest = { ...(message as unknown as Record<string, unknown>) };
    delete rest.attachments;
    return rest as unknown as ChatMessage;
  }
  return { ...message, attachments: clean };
}

function parseMessages(raw: string | null): readonly ChatMessage[] {
  if (raw == null || raw.length === 0) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isPersistedChatMessage)
      .map(sanitizeMessageAttachments)
      .map(sanitizeMessageThinking)
      .map(sanitizeMessageToolCalls);
  } catch {
    return [];
  }
}

function previewBytes(m: ChatMessage): number {
  return (
    m.attachments?.reduce((n, a) => n + (a.previewUrl?.length ?? 0), 0) ?? 0
  );
}

/** Drop a message's thumbnails, keeping the attachment metadata for chips. */
function stripPreviews(m: ChatMessage): ChatMessage {
  return {
    ...m,
    attachments: (m.attachments ?? []).map((a) => {
      if (a.previewUrl == null) return a;
      return {
        type: a.type,
        mimeType: a.mimeType,
        ...(a.fileName != null ? { fileName: a.fileName } : {}),
      };
    }),
  };
}

/**
 * Enforce a per-session byte budget over one unbounded-size field,
 * newest-first: walk backwards spending the budget, and once it is spent every
 * older message loses that field. Strictly newest-wins — the walk latches
 * `exhausted` at the first message the budget cannot cover instead of letting
 * a smaller older one slip in behind a larger newer one, which is what the
 * budgets' doc comments promise.
 *
 * `strip` returns null when the message has nothing left worth persisting
 * without the field; those are dropped from the slice entirely.
 */
function capFieldBytes(
  messages: readonly ChatMessage[],
  budget: number,
  has: (m: ChatMessage) => boolean,
  sizeOf: (m: ChatMessage) => number,
  strip: (m: ChatMessage) => ChatMessage | null,
): readonly ChatMessage[] {
  let remaining = budget;
  let exhausted = false;
  let changed = false;
  const out: (ChatMessage | null)[] = messages.slice();
  for (let i = out.length - 1; i >= 0; i -= 1) {
    const message = out[i]!;
    // `has` is the cheap presence test and `sizeOf` the expensive
    // measurement, kept apart on purpose: past the latch the size is not
    // wanted, only whether there is anything to strip. Measuring anyway put
    // a UTF-8 encode of every remaining trace on a path the persist effect
    // runs once per streamed delta.
    if (!has(message)) continue;
    if (!exhausted) {
      const bytes = sizeOf(message);
      if (bytes <= remaining) {
        remaining -= bytes;
        continue;
      }
      exhausted = true;
    }
    out[i] = strip(message);
    changed = true;
  }
  return changed ? out.filter((m): m is ChatMessage => m != null) : messages;
}

const utf8 = new TextEncoder();

/**
 * UTF-8 byte length. Only ever called on reasoning traces, and only until the
 * budget is spent (see `capFieldBytes`), so the total encoded per call is
 * bounded by the budget plus one trace — not by the transcript's length.
 */
function utf8Length(text: string): number {
  return utf8.encode(text).length;
}

function hasThinking(m: ChatMessage): boolean {
  return m.thinking != null && m.thinking.length > 0;
}

function thinkingBytes(m: ChatMessage): number {
  return m.thinking == null ? 0 : utf8Length(m.thinking);
}

function hasPreviews(m: ChatMessage): boolean {
  return m.attachments?.some((a) => a.previewUrl != null) === true;
}

/** Drop a message's reasoning trace, keeping everything else intact. */
function stripThinking(m: ChatMessage): ChatMessage {
  const rest = { ...(m as unknown as Record<string, unknown>) };
  delete rest.thinking;
  delete rest.isThinking;
  return rest as unknown as ChatMessage;
}

/**
 * True when a message's ONLY payload is its reasoning trace — the shape
 * `applyThinkingUpdate` gives the transcript's thinking block, which carries
 * no answer text of its own. Stripping the trace from one of these leaves an
 * assistant message with nothing to render, and `MessageBubble` draws that as
 * an empty bubble, so they are dropped outright instead.
 */
function isThinkingOnlyMessage(m: ChatMessage): boolean {
  return (
    m.role === 'assistant' &&
    // Optional-chained deliberately: this runs on messages that crossed a
    // remote boundary, and a throw here takes the whole hydration round with
    // it. The boundary validates too; this is the belt.
    (m.content?.length ?? 0) === 0 &&
    m.attachments == null &&
    m.widgetType == null &&
    m.approval == null &&
    m.agentSpec == null &&
    m.ui == null &&
    // Defensive: no record carries both today, but one that did would still
    // render its tool log after the trace was stripped — not a husk.
    (m.toolCalls == null || m.toolCalls.length === 0)
  );
}

/**
 * Coerce a message's `thinking` on entry, for the same reason
 * `sanitizeMessageAttachments` coerces `attachments`: it crosses localStorage
 * and remote sync docs unvalidated (`isPersistedChatMessage` checks only the
 * four required fields), and it lands straight in a React child — so a
 * non-string here, from a corrupt blob or a doc written by a different shape,
 * throws inside the transcript render rather than degrading. Drop the field
 * and keep the message.
 */
export function sanitizeMessageThinking(message: ChatMessage): ChatMessage {
  const raw = (message as { readonly thinking?: unknown }).thinking;
  if (raw === undefined || typeof raw === 'string') return message;
  return stripThinking(message);
}

/** Drop a message's tool log, keeping everything else intact. */
function stripToolCalls(message: ChatMessage): ChatMessage {
  const rest = { ...(message as unknown as Record<string, unknown>) };
  delete rest.toolCalls;
  delete rest.toolCallsDropped;
  delete rest.isToolActive;
  return rest as unknown as ChatMessage;
}

/**
 * Coerce a message's tool log on entry — it crosses localStorage and sync docs
 * unvalidated and lands in a React child (see `sanitizeMessageThinking`).
 *
 * Shape failures drop the field whole: salvaging part of a corrupt log would
 * report a count that never happened. Bounds failures do not — an over-long
 * log is cut to the window's tail with the overflow folded into
 * `toolCallsDropped`, and entries are rebuilt from `TOOL_CALL_KEYS`.
 */
export function sanitizeMessageToolCalls(message: ChatMessage): ChatMessage {
  const raw = (message as { readonly toolCalls?: unknown }).toolCalls;
  if (raw === undefined) return message;
  if (
    !Array.isArray(raw) ||
    !raw.every(
      (entry) =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as { readonly name?: unknown }).name === 'string',
    )
  ) {
    return stripToolCalls(message);
  }

  const entries = raw as readonly { readonly name: string }[];
  const rawDropped = (message as { readonly toolCallsDropped?: unknown })
    .toolCallsDropped;
  // Dropped on its own: the log is still good, and the header falls back to
  // the window's length.
  const droppedIsValid =
    typeof rawDropped === 'number' &&
    Number.isFinite(rawDropped) &&
    rawDropped >= 0;
  const dropped = droppedIsValid ? (rawDropped as number) : 0;

  // Re-enforce the bounds: logs from sync docs and older blobs never went
  // through the capture path that applies them.
  const overflow = Math.max(0, entries.length - TOOL_CALLS_WINDOW);
  const kept = overflow > 0 ? entries.slice(overflow) : entries;
  const needsRebuild =
    overflow > 0 ||
    kept.some(
      (entry) =>
        entry.name.length > TOOL_NAME_MAX_CHARS ||
        Object.keys(entry).some(
          (key) => !(TOOL_CALL_KEYS as readonly string[]).includes(key),
        ),
    );
  if (!needsRebuild && (rawDropped === undefined || droppedIsValid)) {
    return message;
  }

  const next: Record<string, unknown> = {
    ...(message as unknown as Record<string, unknown>),
    toolCalls: kept.map((entry) => {
      // Projected THROUGH `TOOL_CALL_KEYS`, not a literal: a hardcoded
      // `{ name }` would silently drop a newly added field from every row of
      // any log that also trips a bound.
      const source = entry as unknown as Record<string, unknown>;
      const projected: Record<string, unknown> = {};
      for (const key of TOOL_CALL_KEYS) {
        if (source[key] !== undefined) projected[key] = source[key];
      }
      projected.name = entry.name.slice(0, TOOL_NAME_MAX_CHARS);
      return projected as unknown as ToolCall;
    }),
  };
  const totalDropped = dropped + overflow;
  if (totalDropped > 0) next.toolCallsDropped = totalDropped;
  else delete next.toolCallsDropped;
  return next as unknown as ChatMessage;
}

/**
 * Clear the flags that only mean something while a run is in flight. A
 * message carrying them outside that run — read back from storage, or adopted
 * from another device's sync doc — renders as stuck streaming dots or a
 * thinking block that shimmers forever, and no terminator will ever match its
 * `runId` to close it.
 */
export function clearLiveMessageFlags(message: ChatMessage): ChatMessage {
  if (
    message.isStreaming !== true &&
    message.isThinking !== true &&
    message.isToolActive !== true
  ) {
    return message;
  }
  return {
    ...message,
    isStreaming: false,
    isThinking: false,
    // Left set, this would read back as "a tool call is running" on a run that
    // ended before the reload, and — because it is what separates a repeated
    // state frame from a new call — would silently swallow the first tool call
    // of whatever run adopted the record next.
    isToolActive: false,
  };
}

/**
 * Pick the slice of `messages` we want to persist — and the slice we want to
 * UPLOAD, which is the same slice for the same reasons (see
 * `trimMessagesForPersistence` below).
 *
 *   1. Hard-cap the total at `PERSISTED_HISTORY_HARD_CAP`, keeping the most
 *      recent.
 *
 *      There USED to be an age rule above this: drop anything older than 30
 *      days, unless fewer than 50 messages survived that window, in which
 *      case keep the last 50 regardless of age. It is gone, and the reason
 *      matters. This store is no longer a source of truth — the gateway is,
 *      and `loadOlderMessages` pages back through its transcript on demand.
 *      An age rule now only decides which of the pages the user just
 *      scrolled to gets evicted on the next write, so scrolling back and
 *      reloading lost the same history twice. The count cap still bounds the
 *      blob, which is what the quota needs.
 *
 *      It also made the cache lie in a way that was hard to read: a
 *      low-traffic chat fell to the 50-message floor and so showed three
 *      weeks of history, while a busy one showed 30 days and nothing before
 *      it — with no rule the user could infer from either.
 *   2. Clear the live flags — a partially-streamed bubble persisted with
 *      `isStreaming: true` reloads as stuck dots, `isThinking: true` as a
 *      block that shimmers forever.
 *   3. Cap cumulative thumbnail bytes (`PERSISTED_THUMBNAIL_BYTE_BUDGET`) and
 *      reasoning bytes (`PERSISTED_THINKING_BYTE_BUDGET`) — retention is
 *      count-based, so those are the unbounded-size fields a session
 *      accumulates. A run's tool log is bounded by count instead
 *      (`TOOL_CALLS_WINDOW`) and needs no budget here.
 *
 * Step 3 can drop a reasoning-only record outright, so the cap in step 1 is a
 * cap on messages ENTERING the budgets, not on the count that comes out. A
 * record that renders as nothing but a collapsed trace is not conversation.
 */
export function trimMessagesForPersistence(
  messages: readonly ChatMessage[],
): readonly ChatMessage[] {
  if (messages.length === 0) return messages;
  const live = messages
    .slice(-PERSISTED_HISTORY_HARD_CAP)
    .map(clearLiveMessageFlags);
  const thumbed = capFieldBytes(
    live,
    PERSISTED_THUMBNAIL_BYTE_BUDGET,
    hasPreviews,
    previewBytes,
    stripPreviews,
  );
  return capFieldBytes(
    thumbed,
    PERSISTED_THINKING_BYTE_BUDGET,
    hasThinking,
    thinkingBytes,
    // A message whose only payload IS the trace has nothing left to show once
    // it is gone, so drop the record rather than persist a husk that reloads
    // as an empty assistant bubble. It is a real loss, not a deferral: this
    // same trim runs on the merged transcript in `hydrateFromRemote`, so a
    // copy synced back from another device is dropped again on arrival.
    (m) => (isThinkingOnlyMessage(m) ? null : stripThinking(m)),
  );
}

/**
 * The shape of a transcript on the WIRE: `trimMessagesForPersistence` with
 * every reasoning trace removed, and every message that was nothing but a
 * trace dropped.
 *
 * Reasoning stays device-local. It is a collapsed, display-only block, and it
 * is the one field that made a session document grow without bound once
 * thinking was on by default — a few KB per assistant turn, against an 8 MiB
 * document cap whose 413 the sync client treats as permanent, and a 200 MB
 * per-user total whose 507 it retries forever. Neither failure is visible to
 * the user; both present as "my other device shows an older version of this
 * chat". The 256 KB budget bounded the growth; not shipping the trace at all
 * removes the driver.
 *
 * Every path that PUTs a session document goes through here: the provider's
 * persist effect, the local-only migration upload in `hydrateFromRemote`, and
 * the 412 conflict retry. The local store keeps the trace — `mergeMessages` is
 * local-wins by id, so a remote copy without `thinking` never displaces a
 * local one with it, and the conflict path re-merges into the on-disk blob
 * rather than overwriting it with the wire doc.
 */
export function stripThinkingForSync(
  messages: readonly ChatMessage[],
): readonly ChatMessage[] {
  const out: ChatMessage[] = [];
  for (const m of trimMessagesForPersistence(messages)) {
    if (!hasThinking(m)) {
      out.push(m);
      continue;
    }
    if (isThinkingOnlyMessage(m)) continue;
    out.push(stripThinking(m));
  }
  return out;
}

// --- Factory ---

/**
 * How many times any chat's cached rows have been written, this tab's life.
 *
 * Not a timestamp and not per chat: it exists only so a caller holding
 * something DERIVED from the cache can ask "has any of it changed since I
 * looked" in one comparison. See `saveSessionTranscript`.
 *
 * Module scope rather than per store, because a store is cheap and callers
 * build their own: the chat list and the sidebar's search each construct one
 * from the same config, so a per-instance count would leave the searcher
 * blind to every write the chat made. What matters is that the CACHE changed,
 * and the cache is the origin's storage — shared by every instance.
 */
let transcriptWrites = 0;

export function createChatHistoryStore(
  config: ChatHistoryStoreConfig,
): ChatHistoryStore {
  const { keyFor, defaultFolder } = config;
  const twoLevelLegacy = config.legacyMigration === 'two-level';
  const pendingCleanupOn = config.pendingCleanupTracking === true;
  const orphanScanOnClear = config.orphanScanOnClear === true;
  const clearCustomFoldersOnSignOut =
    config.clearCustomFoldersOnSignOut === true;
  // ---- Sessions ----

  function loadSessions(accountId: string): readonly ChatSession[] {
    try {
      const raw = localStorage.getItem(keyFor('sessions', accountId));
      if (raw == null || raw.length === 0) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isPersistedSession);
    } catch {
      return [];
    }
  }

  function saveSessions(
    accountId: string,
    sessions: readonly ChatSession[],
  ): void {
    try {
      localStorage.setItem(
        keyFor('sessions', accountId),
        JSON.stringify(sessions),
      );
    } catch {
      // best-effort
    }
  }

  // ---- Custom folders ----

  function loadCustomFolders(accountId: string): readonly CustomFolder[] {
    try {
      const raw = localStorage.getItem(keyFor('customFolders', accountId));
      if (raw == null || raw.length === 0) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isPersistedCustomFolder);
    } catch {
      return [];
    }
  }

  function saveCustomFolders(
    accountId: string,
    folders: readonly CustomFolder[],
  ): void {
    try {
      localStorage.setItem(
        keyFor('customFolders', accountId),
        JSON.stringify(folders),
      );
    } catch {
      // best-effort
    }
  }

  // ---- Session messages ----

  function loadSessionMessages(
    accountId: string,
    sessionId: string,
  ): readonly ChatMessage[] {
    try {
      return parseMessages(
        localStorage.getItem(keyFor('messages', accountId, sessionId)),
      );
    } catch {
      return [];
    }
  }

  function saveSessionMessages(
    accountId: string,
    sessionId: string,
    messages: readonly ChatMessage[],
  ): readonly ChatMessage[] {
    const trimmed = trimMessagesForPersistence(messages);
    try {
      localStorage.setItem(
        keyFor('messages', accountId, sessionId),
        JSON.stringify(trimmed),
      );
    } catch {
      // Best-effort: intentionally silent. On a write/quota failure the
      // transcript simply stays in memory and is re-fetched/re-merged from the
      // gateway on the next hydration, so there's no data loss — only a missed
      // local cache write. (Core has no logging seam; a host-facing
      // "history not saved locally" signal would belong upstream.)
    }
    // Returned even on a write failure: the caller wants the trimmed shape,
    // and whether the disk write landed doesn't change what that shape is.
    return trimmed;
  }

  function deleteSessionMessages(accountId: string, sessionId: string): void {
    try {
      localStorage.removeItem(keyFor('messages', accountId, sessionId));
      localStorage.removeItem(keyFor('transcript', accountId, sessionId));
    } catch {
      // ignore
    }
  }

  function purgeLegacyTranscripts(accountId: string): void {
    try {
      // Derived from the host's own key shape rather than hardcoded: a
      // trailing empty part leaves the separator, which is exactly the prefix
      // every per-session blob for this account starts with. Works for both
      // shapes hosts use (`<ns>_<scope>:<account>:` and
      // `<account>:<ns>_<scope>:`).
      const prefix = keyFor('messages', accountId, '');
      if (prefix.length === 0) return;
      const doomed: string[] = [];
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key != null && key.startsWith(prefix)) doomed.push(key);
      }
      for (const key of doomed) localStorage.removeItem(key);
    } catch {
      // Best-effort cleanup of a store nothing reads.
    }
  }

  // ---- Transcript paint cache ----
  //
  // The agent's answer to `chat.history`, stored EXACTLY as it arrived, for
  // one job: painting a chat on open instead of showing an empty pane for the
  // second it takes to connect and read. It is never merged, never appended
  // to, and never consulted for anything but that first paint — the next read
  // overwrites it wholesale.
  //
  // Rows go in untransformed on purpose. The moment this holds a shape the UI
  // invented, it stops being a copy of the source of truth and starts being a
  // second opinion about it — which is the thing that has to be reconciled,
  // and reconciliation is what this replaces.

  /**
   * A budget, not a message count: a row can carry a base64 image, so 1500 of
   * them is not a bounded amount of bytes. The newest rows are kept, since the
   * tail is what a chat opens on.
   *
   * PER CHAT, and the quota it defends is per ORIGIN — so this bounds one
   * chat, not the cache. The comment here used to claim it was "shared across
   * every cached chat", which it never was: three chats at the cap measured
   * ~6 MB against a ~5 MB quota, and at that point the write that fails is
   * the NEWEST one, which then deletes itself while the stale oversized ones
   * keep the space. So the ceiling is deliberately low enough that the
   * realistic number of recently-opened chats fits under the quota together,
   * and `evictOldestTranscripts` below makes room rather than letting the
   * newest write lose.
   */
  const TRANSCRIPT_CACHE_BUDGET_BYTES = 1_000_000;

  /**
   * How many other chats' cached copies one write may forget to make room.
   *
   * Bounded so a write that was never going to fit cannot sweep the cache
   * clean on its way to failing anyway.
   */
  const MAX_TRANSCRIPT_EVICTIONS = 5;

  /**
   * Bytes a string costs in storage, not UTF-16 units.
   *
   * The budget above defends a quota browsers count in bytes, and this was
   * compared against `String.length` — which is code units, so anything
   * outside the Latin range was undercounted by up to 3×. A CJK conversation
   * nominally at the cap weighed nearly triple it.
   */
  function storedBytes(payload: string): number {
    if (typeof TextEncoder !== 'undefined') {
      return new TextEncoder().encode(payload).length;
    }
    // Older engines: count the encoding by hand rather than fall back to the
    // measurement that caused the bug.
    let bytes = 0;
    for (const ch of payload) {
      const cp = ch.codePointAt(0) ?? 0;
      bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
    }
    return bytes;
  }

  function loadSessionTranscript(
    accountId: string,
    sessionId: string,
  ): readonly GatewayHistoryRow[] {
    try {
      const raw = localStorage.getItem(
        keyFor('transcript', accountId, sessionId),
      );
      if (raw == null || raw.length === 0) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Rows are the gateway's shape, not ours, so there is nothing to
      // validate beyond "it is an object" — the projection that renders them
      // reads every field defensively and a row it cannot use becomes no
      // bubble. Anything stricter here would silently drop rows for carrying
      // a field this client has not been taught about yet.
      return parsed.filter(
        (row): row is GatewayHistoryRow =>
          row != null && typeof row === 'object',
      );
    } catch {
      return [];
    }
  }

  function hasSessionTranscript(accountId: string, sessionId: string): boolean {
    try {
      const raw = localStorage.getItem(
        keyFor('transcript', accountId, sessionId),
      );
      // A stored empty array is not a conversation. Checked on the string so
      // this stays a cheap test: an empty transcript is never written (the
      // key is removed instead), so this only guards a blob from an older
      // build.
      return raw != null && raw.length > 2;
    } catch {
      return false;
    }
  }

  function saveSessionTranscript(
    accountId: string,
    sessionId: string,
    rows: readonly GatewayHistoryRow[],
  ): void {
    const key = keyFor('transcript', accountId, sessionId);
    // Bumped on every write, whatever the write turns out to be — a store, a
    // removal, or a quota failure that clears the key. Callers that CACHE
    // something derived from these rows (chat search builds an index of
    // them) have no other way to notice: the chat's own `updatedAt` is the
    // roster's field and does not move when a read rewrites the cache
    // underneath it, so a search index keyed on that never refreshed and
    // went on answering from the text a chat held when it was first opened.
    transcriptWrites += 1;
    try {
      if (rows.length === 0) {
        // An empty transcript is a real answer — a chat cleared on another
        // device — so the cache has to stop claiming otherwise.
        localStorage.removeItem(key);
        return;
      }
      let kept = rows;
      let payload = JSON.stringify(kept);
      // Drop from the head ONE ROW AT A TIME until it fits.
      //
      // Halving threw away up to half of what would have fitted — 40 rows of
      // 100 KB kept 10 where 19 fit under the same budget — and this cache
      // exists to give a reopened chat something to scroll, so the rows it
      // discards for no reason are the point of it. Bounded by the row count
      // and each step is one serialise, which is the same work the loop did
      // before; a transcript is capped at 1500 rows.
      while (
        storedBytes(payload) > TRANSCRIPT_CACHE_BUDGET_BYTES &&
        kept.length > 0
      ) {
        kept = kept.slice(1);
        payload = JSON.stringify(kept);
      }
      // A chat whose newest single row is already over budget (one large
      // image) caches nothing rather than throwing: it will paint from the
      // read like any uncached chat.
      if (
        kept.length === 0 ||
        storedBytes(payload) > TRANSCRIPT_CACHE_BUDGET_BYTES
      ) {
        localStorage.removeItem(key);
        return;
      }
      localStorage.setItem(key, payload);
    } catch {
      // Out of room, most likely — the quota is per origin and this cache is
      // one of several things spending it. Make space by dropping the
      // LEAST-RECENTLY-OPENED chats' copies and try once more, because the
      // alternative is what used to happen: the newest write is the one that
      // fails, and it then deletes itself while stale oversized copies of
      // chats nobody has opened in weeks keep the space.
      try {
        localStorage.removeItem(key);
        const payload = JSON.stringify(rows);
        // A few attempts, because one forgotten chat may not be enough and
        // an unbounded loop here would sweep the whole cache for a write
        // that was never going to fit.
        for (
          let attempt = 0;
          attempt < MAX_TRANSCRIPT_EVICTIONS;
          attempt += 1
        ) {
          if (!evictOldestTranscripts(accountId, sessionId)) break;
          try {
            localStorage.setItem(key, payload);
            return;
          } catch {
            // Still short. Forget another one.
          }
        }
        localStorage.removeItem(key);
      } catch {
        // Still no room, or storage is unavailable entirely (private mode).
        // Costs a blank pane for a second on the next open and nothing else:
        // there is no data here the agent does not have.
        try {
          localStorage.removeItem(key);
        } catch {
          // ignore
        }
      }
    }
  }

  /**
   * Free space by forgetting other chats' cached copies. True if any went.
   *
   * Least-recently-updated first, per the chat list's own ordering, and never
   * the chat being written — which is the one the user is looking at.
   *
   * This is the origin-level fallback the budget's own note has been asking
   * for: a per-chat cap cannot bound a per-origin quota, so something has to
   * decide which chat loses when the quota binds. Doing nothing meant the
   * newest write lost, which is the worst possible choice — it is the only
   * copy anyone is about to read.
   */
  function evictOldestTranscripts(
    accountId: string,
    keepSessionId: string,
  ): boolean {
    const ordered = [...loadSessions(accountId)]
      .filter((s) => s.sessionId !== keepSessionId)
      .sort((a, b) => a.updatedAt - b.updatedAt);
    let freed = false;
    for (const s of ordered) {
      const victim = keyFor('transcript', accountId, s.sessionId);
      if (localStorage.getItem(victim) == null) continue;
      localStorage.removeItem(victim);
      freed = true;
      // One at a time, so a chat is only forgotten while the room is still
      // needed. The caller retries its write after this returns.
      return freed;
    }
    return freed;
  }

  // ---- UI state ----

  function loadHistoryUiState(accountId: string): HistoryUiState {
    try {
      const raw = localStorage.getItem(keyFor('historyUi', accountId));
      if (raw == null || raw.length === 0) return DEFAULT_UI_STATE;
      const parsed: unknown = JSON.parse(raw);
      if (parsed == null || typeof parsed !== 'object') return DEFAULT_UI_STATE;
      const obj = parsed as Record<string, unknown>;
      const collapsed = obj.collapsed as Record<string, unknown> | undefined;
      const activeSessionId =
        typeof obj.activeSessionId === 'string' ? obj.activeSessionId : null;
      return {
        collapsed: {
          paradex: collapsed?.paradex === true,
          paradigm: collapsed?.paradigm === true,
        },
        activeSessionId,
      };
    } catch {
      return DEFAULT_UI_STATE;
    }
  }

  function saveHistoryUiState(accountId: string, state: HistoryUiState): void {
    try {
      localStorage.setItem(
        keyFor('historyUi', accountId),
        JSON.stringify(state),
      );
    } catch {
      // ignore
    }
  }

  // ---- Tombstones ----

  function loadTombstones(accountId: string): readonly DeletionTombstone[] {
    try {
      const raw = localStorage.getItem(keyFor('tombstones', accountId));
      if (raw == null || raw.length === 0) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isPersistedTombstone);
    } catch {
      return [];
    }
  }

  function saveTombstones(
    accountId: string,
    tombstones: readonly DeletionTombstone[],
  ): void {
    try {
      localStorage.setItem(
        keyFor('tombstones', accountId),
        JSON.stringify(tombstones),
      );
    } catch {
      // best-effort
    }
  }

  function addTombstone(
    accountId: string,
    kind: DeletionTombstone['kind'],
    id: string,
  ): void {
    const existing = loadTombstones(accountId).filter(
      (t) => !(t.kind === kind && t.id === id),
    );
    saveTombstones(accountId, [
      ...existing,
      { kind, id, deletedAt: Date.now() },
    ]);
  }

  // ---- Legacy migration ----

  /**
   * Read the legacy flat-history blob, optionally with two-level fallback
   * (Desktop's pre-suffix shape).
   */
  function readLegacyHistoryRaw(accountId: string): string | null {
    try {
      const current = localStorage.getItem(keyFor('legacyHistory', accountId));
      if (current != null) return current;
    } catch {
      // continue to very-legacy if enabled
    }
    if (!twoLevelLegacy) return null;
    try {
      const veryLegacyKey = keyFor('veryLegacyHistory', accountId);
      const veryLegacy = localStorage.getItem(veryLegacyKey);
      if (veryLegacy != null) {
        try {
          localStorage.setItem(keyFor('legacyHistory', accountId), veryLegacy);
          localStorage.removeItem(veryLegacyKey);
        } catch {
          // best-effort migration
        }
      }
      return veryLegacy;
    } catch {
      return null;
    }
  }

  /**
   * If a legacy flat-history blob exists for this account but the new
   * sessions index doesn't, migrate it into a single session under the
   * default folder so existing users don't lose their transcript.
   */
  function migrateLegacyHistoryIfNeeded(
    accountId: string,
  ): readonly ChatSession[] {
    const existing = loadSessions(accountId);
    if (existing.length > 0) return existing;
    const legacy = readLegacyHistoryRaw(accountId);
    if (legacy == null || legacy.length === 0) return existing;
    const messages = parseMessages(legacy);
    if (messages.length === 0) {
      try {
        localStorage.removeItem(keyFor('legacyHistory', accountId));
      } catch {
        // ignore
      }
      return existing;
    }
    const now = Date.now();
    const session: ChatSession = {
      sessionId: generateChatSessionId(),
      folder: defaultFolder,
      title: deriveSessionTitleFromMessages(messages, 'Restored chat'),
      pinned: false,
      createdAt: now,
      updatedAt: messages[messages.length - 1]?.timestamp ?? now,
    };
    const migrated = [session];
    saveSessions(accountId, migrated);
    // Into the PAINT CACHE, which is the store the chat actually paints from.
    //
    // This used to write the retired per-session message blob, and nothing
    // reads that any more — so the one user this migration exists for got a
    // "Restored chat" row whose conversation was nowhere, and the cleanup of
    // those retired blobs (which runs on the same tick) deleted the write
    // anyway. Recovering the row and losing the conversation is the failure
    // this function was written to prevent.
    //
    // Rows without `__openclaw` metadata, because there is none to give: this
    // history predates the agent holding it, so there is no agent id or
    // transcript identity to claim. The projection mints a stable id from the
    // position and timestamp for exactly this case, and the first successful
    // read of the chat replaces all of it with the agent's own version.
    saveSessionTranscript(
      accountId,
      session.sessionId,
      messages.map((m) => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp,
      })),
    );
    try {
      localStorage.removeItem(keyFor('legacyHistory', accountId));
    } catch {
      // ignore
    }
    return migrated;
  }

  // ---- Orphan recovery ----

  function recoverOrphanedFolders(
    sessions: readonly ChatSession[],
    existingFolders: readonly CustomFolder[],
  ): {
    readonly folders: readonly CustomFolder[];
    readonly recovered: boolean;
  } {
    const existingIds = new Set(existingFolders.map((f) => f.id));
    const referencedIds = new Set<string>();
    for (const s of sessions) {
      if (s.customFolderId != null && !existingIds.has(s.customFolderId)) {
        referencedIds.add(s.customFolderId);
      }
    }
    if (referencedIds.size === 0) {
      return { folders: existingFolders, recovered: false };
    }
    const now = Date.now();
    let n = 1;
    const orphanFolders: CustomFolder[] = [];
    const sortedIds = [...referencedIds].sort();
    for (const id of sortedIds) {
      orphanFolders.push({
        id,
        name: `Recovered folder ${n}`,
        createdAt: now,
      });
      n += 1;
    }
    return {
      folders: [...existingFolders, ...orphanFolders],
      recovered: true,
    };
  }

  // ---- Clear ----

  /**
   * Wipe everything associated with this account: sessions index, every
   * per-session message blob, UI state, legacy keys, tombstones. Desktop
   * additionally cleans custom folders and runs a defensive prefix scan
   * to catch stray blobs if the index is corrupt.
   */
  function clearChatHistory(accountId: string): void {
    try {
      const sessions = loadSessions(accountId);
      for (const s of sessions) {
        try {
          localStorage.removeItem(keyFor('messages', accountId, s.sessionId));
        } catch {
          // ignore
        }
      }

      if (orphanScanOnClear) {
        // Collect orphan keys first, then delete — avoids index shift while
        // iterating localStorage.
        const prefix = keyFor('messages', accountId, '');
        const orphanKeys: string[] = [];
        try {
          for (let i = 0; i < localStorage.length; i += 1) {
            const key = localStorage.key(i);
            if (key != null && key.startsWith(prefix)) {
              orphanKeys.push(key);
            }
          }
        } catch {
          // ignore
        }
        for (const key of orphanKeys) {
          try {
            localStorage.removeItem(key);
          } catch {
            // ignore
          }
        }
      }

      localStorage.removeItem(keyFor('sessions', accountId));
      localStorage.removeItem(keyFor('historyUi', accountId));
      localStorage.removeItem(keyFor('legacyHistory', accountId));
      if (twoLevelLegacy) {
        try {
          localStorage.removeItem(keyFor('veryLegacyHistory', accountId));
        } catch {
          // ignore
        }
      }
      localStorage.removeItem(keyFor('tombstones', accountId));
      if (clearCustomFoldersOnSignOut) {
        try {
          localStorage.removeItem(keyFor('customFolders', accountId));
        } catch {
          // ignore
        }
      }
    } catch {
      // ignore
    }
  }

  // ---- Pending cleanup (Desktop-only) ----
  //
  // When `pendingCleanupTracking` is off these are no-ops returning empty
  // results so callers don't need to feature-detect.

  // NOTE on age expiry: the Desktop original does NOT filter by age on
  // read. Bounded retention (PENDING_CLEANUP_MAX_AGE_MS) is enforced by
  // the session-sync layer (`processCleanupEntry`), which detects an
  // expired entry and performs the wipe + remove. `loadPendingCleanups`
  // must therefore return ALL valid entries so that logic can see them;
  // filtering here would silently disable the bounded-retention wipe.
  function loadPendingCleanups(): readonly PendingCleanup[] {
    if (!pendingCleanupOn) return [];
    try {
      const raw = localStorage.getItem(keyFor('pendingCleanup', ''));
      if (raw == null || raw.length === 0) return [];
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(isPersistedPendingCleanup);
    } catch {
      return [];
    }
  }

  function savePendingCleanups(entries: readonly PendingCleanup[]): void {
    if (!pendingCleanupOn) return;
    try {
      if (entries.length === 0) {
        localStorage.removeItem(keyFor('pendingCleanup', ''));
      } else {
        localStorage.setItem(
          keyFor('pendingCleanup', ''),
          JSON.stringify(entries),
        );
      }
    } catch {
      // best-effort
    }
  }

  /**
   * Record (or refresh) a pending-cleanup entry for `userId`. Idempotent:
   * a repeat call for the same user updates `signedOutAt` to now and
   * clears the throttle. userId is lowercased so matching is
   * case-insensitive (callers pass mixed-case addresses).
   */
  function addPendingCleanup(userId: string): void {
    if (!pendingCleanupOn) return;
    const lower = userId.toLowerCase();
    const others = loadPendingCleanups().filter((c) => c.userId !== lower);
    savePendingCleanups([
      ...others,
      { userId: lower, signedOutAt: Date.now() },
    ]);
  }

  function removePendingCleanup(userId: string): void {
    if (!pendingCleanupOn) return;
    const lower = userId.toLowerCase();
    savePendingCleanups(
      loadPendingCleanups().filter((c) => c.userId !== lower),
    );
  }

  function updatePendingCleanup(
    userId: string,
    patch: Partial<PendingCleanup>,
  ): void {
    if (!pendingCleanupOn) return;
    const lower = userId.toLowerCase();
    const all = loadPendingCleanups();
    const next = all.map((c) =>
      c.userId === lower ? { ...c, ...patch, userId: c.userId } : c,
    );
    savePendingCleanups(next);
  }

  return {
    loadSessions,
    saveSessions,
    loadCustomFolders,
    saveCustomFolders,
    loadSessionMessages,
    saveSessionMessages,
    deleteSessionMessages,
    purgeLegacyTranscripts,
    loadSessionTranscript,
    hasSessionTranscript,
    saveSessionTranscript,
    transcriptCacheVersion: () => transcriptWrites,
    loadHistoryUiState,
    saveHistoryUiState,
    loadTombstones,
    saveTombstones,
    addTombstone,
    migrateLegacyHistoryIfNeeded,
    recoverOrphanedFolders,
    clearChatHistory,
    loadPendingCleanups,
    addPendingCleanup,
    removePendingCleanup,
    updatePendingCleanup,
  };
}
